import "./toolbar.css";

import { mm } from "@inovitas/infra3dsdk";
import { Box } from "@mui/material";
import React, { useEffect } from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore, Topics } from "shared/common/stores/sidebarStore";
import { useTranslation } from "react-i18next";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";

interface props {
  children: JSX.Element | JSX.Element[];
}

export default function Toolbar({ children }: props) {
  const viewer = defaultStore((state) => state.viewer);
  const showStreetMap = defaultStore((state) => state.showStreetMap);
  const showAxisscale = defaultStore((state) => state.showAxisscale);
  const authMode = defaultStore((state) => state.authMode);

  const imageInfoPopover = sidebarStore((state) => state.imageInfoPopover);
  const topicPopover = sidebarStore((state) => state.topicPopover);
  const infosPopover = sidebarStore((state) => state.infosPopover);
  const measurementPopover = sidebarStore((state) => state.measurementPopover);
  const editPopover = sidebarStore((state) => state.editPopover);
  const changeTopics = sidebarStore((state) => state.changeTopics);
  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeLayerGroups = sidebarStore((state) => state.changeLayerGroups);
  const selectTools = sidebarStore((state) => state.selectTools);
  const showDescription = sidebarStore((state) => state.showDescription);
  const changeUpdateFeaturesList = sidebarStore(
    (state) => state.changeUpdateFeaturesList
  );
  const maximizeTable = sidebarStore((state) => state.maximizeTable);
  const axisControlPopover = sidebarStore((state) => state.axisControlPopover);

  const { t } = useTranslation();

  //enable or disable select tool
  useEffect(() => {
    if (selectTools !== undefined) {
      if (infosPopover) {
        selectTools.enable();
        const axisPickTool = sidebarStore.getState().axisPickTool;
        if (axisPickTool && axisPickTool.enabled) axisPickTool.disable();
      } else {
        const activelayer = viewer
          ?.getScene()
          .getLayerHandler()
          .getActiveLayer();
        if (activelayer) {
          activelayer.featureselector.clearSelection();
        }
        selectTools.disable();
        const axisPickTool = sidebarStore.getState().axisPickTool;
        if (axisPickTool && !axisPickTool.enabled) axisPickTool.enable();
      }
    }
  }, [infosPopover]);

  // update layers list and group while changing a layer
  const updateLayerTree = () => {
    if (viewer === undefined) return;
    const layersArray: Topics[] = [];
    const groups: string[] = layerGroups;
    const visibleLayers = viewer
      .getScene()
      .getLayerHandler()
      .layers.filter((layer: mm.Layer) => !layer.options.hideinlayertree);

    // Snapping state is managed exclusively by the layerupdated event handler
    // in LayerContent — read it from the current store to avoid overwriting it.
    const currentTopics = sidebarStore.getState().topics;

    visibleLayers.forEach((layer: mm.Layer) => {
      const existing = currentTopics.find((t) => t.layer?.id === layer.id);
      const snappingEnabled = existing
        ? existing.snappingEnabled
        : !!viewer
            .getScene()
            .getToolHandler()
            .snaplayers?.includes(layer.getFeatureCollection());

      layersArray.push({
        addedFromUser: layer.addedFromUser,
        name: layer.layername,
        title: layer.title,
        folder: false,
        visible: layer.getVisible(),
        snappingEnabled,
        layer,
      });

      if (
        layer.addedFromUser &&
        layer.topic !== "" &&
        !groups.includes(layer.topic)
      ) {
        groups.push(layer.topic);
      }
    });
    changeTopics(layersArray);
    changeLayerGroups(groups);
  };

  //update layer
  const updateLayerLocalStorage = (
    layer: mm.Layer,
    changes: mm.ILayerChanges
  ) => {
    if (layer.addedFromUser) {
      let onlyDataChanged = false;
      const allLayersAddedFromUser = getLayersAddedByUser();
      allLayersAddedFromUser.forEach((item) => {
        item.layers?.forEach((ls) => {
          if (ls.id === layer.id) {
            if (changes.options?.title !== undefined) {
              ls.title = layer.title;
            }
            if (changes.options?.topic !== undefined) {
              ls.topic = layer.topic;
            }
            if (changes.options?.style !== undefined) {
              ls.style = JSON.parse(layer.style.toJSON());
            }
            if (changes.options?.propertydef !== undefined) {
              ls.propertydef = layer.propertydef;
            }
            if (layer instanceof mm.MemoryLayer && layer.data) {
              (ls as mm.IMemoryLayerOptions).data = layer.data;
              onlyDataChanged = true;
            }
          }
        });
      });
      saveLayersToLocalStorage(
        allLayersAddedFromUser,
        onlyDataChanged ? undefined : t("alert_success.layer_edited")
      );
    }
  };

  const onLayerAdded = () => {
    updateLayerTree();
  };

  const onLayerUpdated = (layer: mm.Layer, changes: mm.ILayerChanges) => {
    if (
      layer instanceof mm.MemoryLayer &&
      (changes.added || changes.updated || changes.removed || changes.options)
    ) {
      updateLayerLocalStorage(layer, changes);
    }

    if (
      changes.visibility ||
      changes.options?.title !== undefined ||
      changes.options?.topic !== undefined ||
      changes.options?.style !== undefined
    ) {
      updateLayerTree();
    }

    if (
      changes.added ||
      changes.updated ||
      changes.removed ||
      changes.options?.propertydef !== undefined
    ) {
      changeUpdateFeaturesList(true);
    }

    if (changes.options) {
      updateLayerLocalStorage(layer, changes);
    }
  };

  const onLayerRemoved = () => {
    updateLayerTree();
  };

  const onLayerSaved = (
    layer: mm.Layer,
    inserts: any,
    updates: any,
    deletions: any
  ) => {
    changeUpdateFeaturesList(true);
  };

  // add topics list from viewer
  useEffect(() => {
    if (!viewer) return;
    updateLayerTree();
    const layer_added_nr = viewer
      .getScene()
      .userInputHandler.subscribeEvent(
        mm.USERINPUTTYPE.LAYERADDED,
        onLayerAdded,
        undefined
      );
    const layer_removed_nr = viewer
      .getScene()
      .userInputHandler.subscribeEvent(
        mm.USERINPUTTYPE.LAYERREMOVED,
        onLayerRemoved,
        undefined
      );
    const layer_updated_nr = viewer
      .getScene()
      .userInputHandler.subscribeEvent(
        mm.USERINPUTTYPE.LAYERUPDATED,
        onLayerUpdated,
        undefined
      );
    const layer_saved_nr = viewer
      .getScene()
      .userInputHandler.subscribeEvent(
        mm.USERINPUTTYPE.LAYERSAVED,
        onLayerSaved,
        undefined
      );

    return () => {
      if (layer_added_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERADDED,
            layer_added_nr
          );
      if (layer_removed_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERREMOVED,
            layer_removed_nr
          );
      if (layer_updated_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERUPDATED,
            layer_updated_nr
          );
      if (layer_saved_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERSAVED,
            layer_saved_nr
          );
    };
  }, [viewer]);

  const toolbarToolsFullHeight =
    maximizeTable ||
    (showDescription && measurementPopover) ||
    topicPopover ||
    axisControlPopover ||
    editPopover;

  const toolbarToolViewHeight =
    topicPopover || imageInfoPopover || axisControlPopover;

  let minHeight = "0px";
  if (toolbarToolsFullHeight) {
    minHeight = "calc(100% - 32px)";
  } else if (toolbarToolViewHeight) {
    if (authMode === "view") {
      minHeight = "396px";
    } else {
      minHeight = showAxisscale ? "444px" : "396px";
    }
  }

  return (
    <Box
      sx={{
        paddingX: 1,
        paddingY: 2,
        display: showStreetMap ? "none" : "flex",
        flexDirection: "column",
        gap: 1,
        width: "48px",
        minHeight: minHeight,
        transition: "min-height 0.3s",
        backgroundColor: "darkblue.main",
        borderRadius: "0px 10px 10px 0px",
        pointerEvents: "auto",
      }}
    >
      {children}
    </Box>
  );
}
